import React, { useEffect, useState } from 'react';
import { checkIsAdmin, getUserTickets, markTicketAsRead } from '../utils/supportUtils';
import TicketList from './TicketList';
import TicketDetail from './TicketDetail';
import CreateTicket from './CreateTicket';

const SupportCenter = ({ user }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentView, setCurrentView] = useState('list');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [adminCheckLoading, setAdminCheckLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (user && !hasInitialized) {
      initializeSupport();
    }
  }, [user, hasInitialized]);

  const initializeSupport = async () => {
    if (!user || hasInitialized) return;

    try {
      setAdminCheckLoading(true);
      const adminStatus = await checkIsAdmin(user.email);
      setIsAdmin(adminStatus);
      setAdminCheckLoading(false);

      await loadTickets(adminStatus);
      setHasInitialized(true);
    } catch (initError) {
      console.error('Error initializing support:', initError);
      setError('Failed to initialize support system');
      setAdminCheckLoading(false);
      setLoading(false);
    }
  };

  const loadTickets = async (adminStatus = isAdmin) => {
    setLoading(true);
    setError('');

    try {
      const { data, error: fetchError } = await getUserTickets(user.id, user.email, adminStatus);
      if (fetchError) {
        setError('Failed to load tickets');
        return;
      }
      setTickets(data || []);
    } catch (loadError) {
      console.error('Error loading tickets:', loadError);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleTicketSelect = async (ticket) => {
    setSelectedTicket(ticket);
    setCurrentView('detail');

    if (user) {
      await markTicketAsRead(ticket.id, user.id);
      window.dispatchEvent(new CustomEvent('ticketRead'));
    }
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedTicket(null);
    loadTickets();
  };

  const handleTicketCreated = (newTicket) => {
    setCurrentView('list');
    setTickets((prev) => [newTicket, ...prev]);
  };

  const handleTicketUpdated = (updatedTicket) => {
    setTickets((prev) =>
      prev.map((ticket) => (ticket.id === updatedTicket.id ? { ...ticket, ...updatedTicket } : ticket))
    );
  };

  if (adminCheckLoading) {
    return (
      <main className="app-page">
        <section className="card">
          <div className="status-card">
            <div className="spinner" />
            <div>
              <h3>Loading support center</h3>
              <p>Preparing your tickets and permissions...</p>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="app-page space-y-6">
      <section className="card">
        <div className="page-head">
          <div>
            <p className="eyebrow">Help Desk</p>
            <h1 className="page-title">{isAdmin ? 'Support Admin Panel' : 'Support Center'}</h1>
            <p className="page-subtitle">
              {isAdmin
                ? 'Review and respond to incoming user tickets.'
                : 'Get help, report issues, and track ticket progress.'}
            </p>
          </div>
          <div className="pill-row">
            {isAdmin && <span className="soft-pill">Admin Mode</span>}
            <span className="soft-pill">{tickets.length} tickets</span>
            {!isAdmin && (
              <button onClick={() => setCurrentView('create')} className="btn-primary">
                Create New Ticket
              </button>
            )}
          </div>
        </div>
      </section>

      {error && (
        <section className="error-banner">
          <h3>Support Error</h3>
          <p>{error}</p>
        </section>
      )}

      {currentView === 'list' && (
        <TicketList
          tickets={tickets}
          onTicketSelect={handleTicketSelect}
          isAdmin={isAdmin}
          loading={loading}
          user={user}
        />
      )}

      {currentView === 'detail' && selectedTicket && (
        <TicketDetail
          ticketId={selectedTicket.id}
          user={user}
          isAdmin={isAdmin}
          onBack={handleBackToList}
          onTicketUpdated={handleTicketUpdated}
        />
      )}

      {currentView === 'create' && (
        <CreateTicket user={user} onTicketCreated={handleTicketCreated} onCancel={() => setCurrentView('list')} />
      )}

      {isAdmin && currentView === 'list' && tickets.length > 0 && (
        <section className="card">
          <div className="metrics-grid">
            <div className="metric-box"><span>Total</span><strong>{tickets.length}</strong></div>
            <div className="metric-box"><span>Pending</span><strong>{tickets.filter((t) => t.status === 'pending').length}</strong></div>
            <div className="metric-box"><span>In Progress</span><strong>{tickets.filter((t) => t.status === 'in_progress').length}</strong></div>
            <div className="metric-box"><span>Resolved</span><strong>{tickets.filter((t) => t.status === 'resolved').length}</strong></div>
          </div>
        </section>
      )}
    </main>
  );
};

export default SupportCenter;
